import type { TagItem, TagState } from '../interface/layout/tagsView.interface';
import type { PayloadAction } from '@reduxjs/toolkit';

import { createSlice, Middleware } from '@reduxjs/toolkit';

const savedTags = localStorage.getItem('tagsViewState');

const defaultInitialState: TagState = {
  activeTagId: window.location.pathname,
  tags: [],
  initializedPaths: [],
};

const initialState: TagState = savedTags
  ? { ...defaultInitialState, ...JSON.parse(savedTags) }
  : defaultInitialState;

const tagsViewSlice = createSlice({
  name: 'tagsView',
  initialState,
  reducers: {
    setActiveTag(state, action: PayloadAction<string>) {
      state.activeTagId = action.payload;
    },

    // ✅ Обновлённая логика addTag — теперь перезаписывает параметры, если тег уже есть
    addTag(state, action: PayloadAction<TagItem>) {
      const existingIndex = state.tags.findIndex((tag) => tag.path === action.payload.path);
      if (existingIndex !== -1) {
        // Обновляем существующий тег
        state.tags[existingIndex] = {
          ...state.tags[existingIndex],
          ...action.payload,
          label: {
            ...state.tags[existingIndex].label,
            ...action.payload.label,
          },
        };
      } else {
        // Добавляем новый тег
        state.tags.push(action.payload);
      }

      // Устанавливаем активный
      state.activeTagId = action.payload.path;
    },

    removeTag(state, action: PayloadAction<string>) {
      const targetKey = action.payload;

      // Не удаляем первый тег (например, dashboard)
      if (targetKey === state.tags[0]?.path) {
        return;
      }

      const activeTagId = state.activeTagId;
      let lastIndex = 0;

      state.tags.forEach((tag, i) => {
        if (tag.path === targetKey) {
          state.tags.splice(i, 1);
          lastIndex = i - 1;
        }
      });

      const tagList = state.tags.filter((tag) => tag.path !== targetKey);

      if (tagList.length && activeTagId === targetKey) {
        state.activeTagId = tagList[lastIndex >= 0 ? lastIndex : 0].path;
      }
    },

    removeAllTag(state) {
      state.activeTagId = state.tags[0]?.path ?? '/';
      state.tags = state.tags[0] ? [state.tags[0]] : [];
    },

    removeOtherTag(state) {
      const activeTag = state.tags.find((tag) => tag.path === state.activeTagId);
      const dashboardTag = state.tags[0];

      state.tags =
        dashboardTag && activeTag && dashboardTag.path !== activeTag.path
          ? [dashboardTag, activeTag]
          : dashboardTag
          ? [dashboardTag]
          : [];
    },

    resetTagState(state, action: PayloadAction<string>) {
      const path = action.payload;
      state.tags = state.tags.filter((tag) => tag.path !== path);
      state.initializedPaths = state.initializedPaths.filter((p) => p !== path);

      if (state.activeTagId === path) {
        state.activeTagId = state.tags[0]?.path || '/';
      }
    },

    markPathInitialized(state, action: PayloadAction<string>) {
      if (!state.initializedPaths.includes(action.payload)) {
        state.initializedPaths.push(action.payload);
      }
    },
  },
});

// ✅ Middleware для сохранения в localStorage
export const localStorageMiddleware: Middleware = (store) => (next) => (action) => {
  const result = next(action);
  const state = store.getState().tagsView;
  localStorage.setItem('tagsViewState', JSON.stringify(state));
  return result;
};

export const {
  setActiveTag,
  addTag,
  removeTag,
  removeAllTag,
  removeOtherTag,
  markPathInitialized,
  resetTagState,
} = tagsViewSlice.actions;

export default tagsViewSlice.reducer;
